import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleSchema from '@/components/feature/ArticleSchema';
import AnimateSection from '@/components/base/AnimateSection';
import { allArticles, seoArticle } from '@/mocks/seo-content';
import NotFound from '@/pages/NotFound';

function getArticle(slug: string | undefined) {
  const found = allArticles.find((a) => a.slug === slug);
  if (found) return found;
  if (slug === 'hoobuyspreadsheet-guide' || !slug) return null;
  return null;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticle(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  const content = article.slug === 'hoobuyspreadsheet-guide' || article.content.length === 0
    ? seoArticle.content
    : article.content;

  return (
    <main className="min-h-screen bg-background-50">
      <ArticleSchema
        slug={article.slug}
        title={article.title}
        description={article.metaDescription || article.description}
      />
      <Navbar />

      <section className="relative w-full h-[420px] md:h-[520px] flex items-end overflow-hidden">
        <img
          src={article.heroImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/15"></div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-10 pb-10 md:pb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-background-50/70 hover:text-background-50 text-sm transition-colors mb-4 whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-xs"></i>
            </div>
            Início
          </Link>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-light text-background-50 leading-tight max-w-[800px]">
            {article.title}
          </h1>
        </div>
      </section>

      <article className="relative w-full py-16 md:py-20">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <AnimateSection>
          <div className="space-y-6">
            {content.map((block, idx) => {
              if (block.type === 'heading') {
                return (
                  <h2
                    key={idx}
                    className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 pt-4 mt-2"
                  >
                    {block.text}
                  </h2>
                );
              }
              return (
                <p
                  key={idx}
                  className="text-base md:text-lg text-foreground-600 font-light leading-relaxed"
                >
                  {block.text}
                </p>
              );
            })}
          </div>
          </AnimateSection>

          <hr className="my-10 border-background-200" />

          <AnimateSection>
          <div className="bg-foreground-900 rounded-2xl p-6 md:p-8 mb-8">
            <h3 className="text-lg font-heading font-semibold text-background-50 mb-4">
              Comparação de Plataformas
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-foreground-700">
                    <th className="text-left py-3 pr-4 text-background-50 font-medium whitespace-nowrap">Plataforma</th>
                    <th className="text-left py-3 pr-4 text-background-50 font-medium whitespace-nowrap">Velocidade</th>
                    <th className="text-left py-3 pr-4 text-background-50 font-medium whitespace-nowrap">Preço</th>
                    <th className="text-left py-3 pr-4 text-background-50 font-medium whitespace-nowrap">Facilidade</th>
                    <th className="text-left py-3 text-background-50 font-medium whitespace-nowrap">SEO Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-foreground-700/30">
                    <td className="py-3 pr-4 text-accent-400 font-medium whitespace-nowrap">HoobuySpreadsheet</td>
                    <td className="py-3 pr-4 text-foreground-200 whitespace-nowrap">⭐⭐⭐⭐⭐</td>
                    <td className="py-3 pr-4 text-foreground-200 whitespace-nowrap">Baixo</td>
                    <td className="py-3 pr-4 text-foreground-200 whitespace-nowrap">Simples</td>
                    <td className="py-3 text-foreground-200 whitespace-nowrap">95</td>
                  </tr>
                  <tr className="border-b border-foreground-700/30">
                    <td className="py-3 pr-4 text-foreground-300 whitespace-nowrap">Pandabuy</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">⭐⭐⭐⭐</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">Médio</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">Complexo</td>
                    <td className="py-3 text-foreground-400 whitespace-nowrap">88</td>
                  </tr>
                  <tr className="border-b border-foreground-700/30">
                    <td className="py-3 pr-4 text-foreground-300 whitespace-nowrap">Sugargoo</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">⭐⭐⭐⭐</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">Médio</td>
                    <td className="py-3 pr-4 text-foreground-400 whitespace-nowrap">Médio</td>
                    <td className="py-3 text-foreground-400 whitespace-nowrap">82</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          </AnimateSection>

          <div className="text-center py-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              Voltar à página inicial
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}